import { useCallback, useEffect, useRef } from 'react';
import { gestureEvents } from './gestureEvents.js';
const emptyBounds = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
};
class BoundsRegistry {
    #entries = new Map();
    #remeasureThrottles = new WeakMap();
    #unsubs = [];
    get __entries() {
        return this.#entries;
    }
    #intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(this.#updateElementVisibility);
    });
    constructor() {
        // dev: cleanup any leftovers from HMR, etc
        window.bounds?.destroy?.();
        window.bounds = this;
    }
    setup = () => {
        this.#unsubs = [
            gestureEvents.subscribe('move', this.#onGestureChange),
            gestureEvents.subscribe('start', this.#onGestureChange),
        ];
        return this.destroy;
    };
    destroy = () => {
        this.#unsubs.forEach((unsub) => unsub());
    };
    bind = (id, element, priority = 0) => {
        if (!element) {
            const entry = this.#entries.get(id);
            if (entry?.element === element) {
                this.#intersectionObserver.unobserve(element);
                this.#entries.delete(id);
            }
            return;
        }
        // set our id data attr
        if (!element.dataset.boundsId) {
            element.dataset.boundsId = id;
        }
        // if we already have an entry, update it
        if (this.#entries.has(id)) {
            const entry = this.#entries.get(id);
            if (entry) {
                if (entry.element === element) {
                    return;
                }
                this.#intersectionObserver.unobserve(entry.element);
                this.#intersectionObserver.observe(element);
                entry.element = element;
                entry.priority = priority;
                this.#applyBounds(entry);
            }
        }
        // otherwise create a new entry
        else {
            const entry = {
                element,
                bounds: emptyBounds,
                visible: true,
                tags: {},
                id,
                priority,
                measuredAt: Date.now(),
            };
            this.#applyBounds(entry);
            this.#entries.set(id, entry);
            this.#intersectionObserver.observe(element);
        }
    };
    addTags = (id, tags) => {
        const entry = this.#entries.get(id);
        if (entry) {
            tags.forEach((tag) => (entry.tags[tag] = (entry.tags[tag] || 0) + 1));
        }
    };
    removeTags = (id, tags) => {
        const entry = this.#entries.get(id);
        if (entry) {
            tags.forEach((tag) => {
                if (entry.tags[tag]) {
                    entry.tags[tag]--;
                    if (entry.tags[tag] === 0) {
                        delete entry.tags[tag];
                    }
                }
            });
        }
    };
    getEntry = (id) => {
        return this.#entries.get(id);
    };
    getByTag = (tag) => {
        const entries = [];
        this.#entries.forEach((entry) => {
            if (entry.tags[tag]) {
                entries.push(entry);
            }
        });
        return entries;
    };
    measure = (id) => {
        const entry = this.#entries.get(id);
        if (entry) {
            this.#measureElement(entry);
        }
    };
    measureAll = (tag) => {
        this.#entries.forEach((entry) => {
            if (entry.visible && (!tag || entry.tags[tag])) {
                this.#measureElement(entry);
            }
        });
    };
    #getElementId = (element) => {
        return element.dataset.boundsId;
    };
    #measureElement = (entry) => {
        let invoker = this.#remeasureThrottles.get(entry);
        if (!invoker) {
            invoker = throttled(() => {
                entry.bounds = entry.element.getBoundingClientRect();
                entry.measuredAt = Date.now();
            }, 500);
            this.#remeasureThrottles.set(entry, invoker);
        }
        invoker();
    };
    #applyBounds = (entry) => {
        entry.bounds = entry.element.getBoundingClientRect();
        entry.measuredAt = Date.now();
    };
    // automatically update drag-interactive bounds when gesture is active
    #onGestureChange = (gesture) => {
        if (gesture.claimId) {
            this.measureAll(gesture.targetTag);
        }
    };
    #updateElementVisibility = (entry) => {
        const id = this.#getElementId(entry.target);
        if (id) {
            const boundsEntry = this.#entries.get(id);
            if (boundsEntry) {
                boundsEntry.visible = entry.isIntersecting;
                if (entry.isIntersecting) {
                    this.#measureElement(boundsEntry);
                }
            }
        }
    };
    #calculateOverlappedArea = (rect1, rect2) => {
        const xOverlap = Math.max(0, Math.min(rect1.right, rect2.right) - Math.max(rect1.left, rect2.left));
        const yOverlap = Math.max(0, Math.min(rect1.bottom, rect2.bottom) - Math.max(rect1.top, rect2.top));
        return xOverlap * yOverlap;
    };
    getOverlappingRegions = (rect, { tag, areaThreshold = 0 } = {}) => {
        const overlaps = [];
        this.#entries.forEach((entry, id) => {
            const region = entry.bounds;
            const overlappedArea = this.#calculateOverlappedArea(rect, region);
            if (overlappedArea > areaThreshold) {
                if (tag) {
                    if (entry.tags[tag]) {
                        overlaps.push({ overlappedArea, ...entry });
                    }
                }
                else {
                    overlaps.push({ overlappedArea, ...entry });
                }
            }
        });
        return overlaps.sort((a, b) => b.overlappedArea - a.overlappedArea);
    };
    getContainingRegions = (point, { tag } = {}) => {
        const containing = [];
        this.#entries.forEach((entry, id) => {
            const rect = entry.bounds;
            if (point.x >= rect.left &&
                point.x <= rect.right &&
                point.y >= rect.top &&
                point.y <= rect.bottom) {
                if (tag) {
                    if (entry.tags[tag]) {
                        containing.push(entry);
                    }
                }
                else {
                    containing.push(entry);
                }
            }
        });
        return containing;
    };
    entryContainsPoint = (id, point) => {
        const entry = this.#entries.get(id);
        if (!entry) {
            return false;
        }
        const rect = entry.bounds;
        return (point.x >= rect.left &&
            point.x <= rect.right &&
            point.y >= rect.top &&
            point.y <= rect.bottom);
    };
}
export const boundsRegistry = new BoundsRegistry();
export function useBindBounds(id, priority = 0) {
    return useCallback((element) => {
        boundsRegistry.bind(id, element, priority);
    }, [id]);
}
export function useTagBounds(id, tags, skip) {
    const tagsRef = useRef(tags);
    tagsRef.current = tags;
    const updateKey = tags.sort().join('|');
    return useEffect(() => {
        if (!skip) {
            const tags = tagsRef.current;
            boundsRegistry.addTags(id, tags);
            return () => {
                boundsRegistry.removeTags(id, tags);
            };
        }
    }, [id, updateKey, skip]);
}
function throttled(func, limit) {
    let lastFunc = undefined;
    let lastRan = 0;
    function throttled(...args) {
        if (Date.now() - lastRan >= limit) {
            if (lastFunc) {
                clearTimeout(lastFunc);
            }
            func(...args);
            lastRan = Date.now();
        }
        else {
            clearTimeout(lastFunc);
            lastFunc = setTimeout(() => {
                func(...args);
                lastRan = Date.now();
            }, limit - (Date.now() - lastRan));
        }
    }
    throttled.cancel = () => {
        if (lastFunc) {
            clearTimeout(lastFunc);
            lastFunc = undefined;
        }
    };
    return throttled;
}
//# sourceMappingURL=bounds.js.map